import { Button } from '@asgard/ui-library';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FormCheckbox } from 'components/form/FormCheckbox';
import { FormPhone } from 'components/form/FormPhone';
import { FormSelect } from 'components/form/FormSelect';
import { FormText } from 'components/form/FormText/FormText';
import { FormTextarea } from 'components/form/FormTextarea';
import HtmlTrans from 'components/shared/HtmlTrans';
import { LinkText } from 'components/shared/LinkText';
import { Heading2 } from 'components/shared/Typography.styled';
import { PHONE_NUMBER_DEFAULT_COUNTRY } from 'features/portal/v2/contact/basic-info/ContactBasicInfo.constants';
import { Trans } from 'next-i18next';
import { FormProvider } from 'react-hook-form';
import { APP_URLS, getAppUrl } from 'utils/app-urls';
import { getPublicPath } from 'utils/paths';
import { useConsultancyForm } from './Form.hooks';
import {
  StyledContainer,
  StyledHeader,
  StyledHeroImage,
  StyledTerms,
  StyledWrapper
} from './Form.styled';
import { getTypeOptions } from './Form.utils';

const Form: React.FC = () => {
  const {
    t,
    formMethods,
    locale,
    onSubmit,
    isTermsAccepted,
    onFormFieldFocus
  } = useConsultancyForm();

  const {
    handleSubmit,
    formState: { isSubmitting }
  } = formMethods;

  return (
    <StyledWrapper>
      <StyledContainer onSubmit={handleSubmit(onSubmit)}>
        <StyledHeader>
          <Heading2 editorial>{t('consultancy.form.title')}</Heading2>
          <p>
            <HtmlTrans ns="consultancy" i18nKey="consultancy.form.desc" />
          </p>
        </StyledHeader>
        <FormProvider {...formMethods}>
          <FormText
            name="name"
            formGroup={{
              label: t('consultancy.form.name')
            }}
            input={{
              placeholder: t('consultancy.form.name_placeholder'),
              onFocus: onFormFieldFocus
            }}
          />
          <FormText
            name="email"
            formGroup={{
              label: t('consultancy.form.email')
            }}
            input={{
              placeholder: t('consultancy.form.email_placeholder'),
              onFocus: onFormFieldFocus
            }}
          />
          <div className="phoneNumber">
            <FormPhone
              name="contactPhone"
              formGroup={{
                label: t('consultancy.form.phone')
              }}
              input={{
                defaultCountry: PHONE_NUMBER_DEFAULT_COUNTRY,
                onFocus: onFormFieldFocus
              }}
            />
          </div>
          <FormText
            name="company"
            formGroup={{
              label: t('consultancy.form.company')
            }}
            input={{
              placeholder: t('consultancy.form.company_placeholder'),
              onFocus: onFormFieldFocus
            }}
          />
          <FormSelect
            name="howDoYouKnowUs"
            formGroup={{
              label: t('consultancy.form.how-do-you-know-us')
            }}
            options={getTypeOptions(t)}
          />
          <FormTextarea
            name="description"
            formGroup={{
              label: t('consultancy.form.description')
            }}
            input={{
              placeholder: t('consultancy.form.description_placeholder'),
              onFocus: onFormFieldFocus,
              rows: 3
            }}
          />
          <FormCheckbox
            name="terms"
            input={{
              onFocus: onFormFieldFocus,
              label: (
                <StyledTerms>
                  <Trans
                    ns={'consultancy'}
                    i18nKey={'consultancy.form.terms'}
                    components={{
                      privatePolicy: (
                        <LinkText
                          href={getAppUrl(APP_URLS.privacyPolicy, locale)}
                        />
                      ),
                      terms: (
                        <LinkText
                          href={getAppUrl(APP_URLS.termsOfUse, locale)}
                        />
                      )
                    }}
                  ></Trans>
                </StyledTerms>
              )
            }}
          />

          <FormCheckbox
            name="isSubscribeToNewsletter"
            input={{
              onFocus: onFormFieldFocus,
              label: (
                <StyledTerms>
                  <Trans
                    ns={'consultancy'}
                    i18nKey={'consultancy.form.newsletter'}
                    components={{
                      newsletter: (
                        <a
                          href="https://none.clickacm.com/show/qgJ2lZXPBBizIfJi3pmS/"
                          target="_blank"
                        />
                      )
                    }}
                  ></Trans>
                </StyledTerms>
              )
            }}
          />
          <Button
            schema="brand2"
            label={t('consultancy.form.submit')}
            startIcon={faCheckCircle}
            type="submit"
            disabled={!isTermsAccepted}
            isLoading={isSubmitting}
            loadingText={t('consultancy.form.submit_in-progress')}
          />
        </FormProvider>
      </StyledContainer>
      <StyledHeroImage>
        <img
          src={getPublicPath('/images/consultancy/planet.svg')}
          alt=""
          className="planet"
        />
        <img
          src={getPublicPath('/images/consultancy/astronaut.svg')}
          alt=""
          className="astronaut"
        />
      </StyledHeroImage>
    </StyledWrapper>
  );
};

export default Form;
